import { Box, InfiniteScroll, SearchField, Text } from '@factorialco/deprecated-design-system'
import { compact } from 'lodash'

import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { useMessageInboxConversations } from '../useMessageInboxConversations'
import { ConversationItemSkeleton } from './ConversationItemSkeleton'
import { ConversationsItem } from './ConversationsItem'
import { useConversationsFilter, useFilteredConversations } from './useConversationsFilter'

export const ConversationsList = () => {
  const { access } = useCurrentSession()
  const { filters, serverFilters, onFiltersChange, options } = useConversationsFilter()

  const { conversations, loadingConversations, hasMoreConversations, fetchMoreConversations } =
    useMessageInboxConversations({
      accessId: access.id,
      watcher: serverFilters.watcher,
      archived: serverFilters.archived,
    })

  const { filteredConversations, totalConversationsCount } = useFilteredConversations(
    compact(conversations.map((c) => c.node)),
    filters
  )

  return (
    <Box maxHeight="full" minWidth="s400" width="s400">
      <Box
        width="full"
        gap="s8"
        paddingY="s16"
        paddingX="s24"
        background="grey200"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
          bottom: { width: 's1', color: 'grey400', style: 'hidden' },
        }}
        borderRadius={{ topLeft: 'abs008', topRight: 'abs008' }}
      >
        <Box flexDirection="row" gap="s8" alignItems="center" justifyContent="spaceBetween">
          <Box width="s200">
            <SelectField
              value={filters.watcher}
              onChange={(value) => onFiltersChange({ watcher: value })}
              options={options.watcher}
            />
          </Box>
          <SelectField
            value={filters.status}
            onChange={(value) => onFiltersChange({ status: value })}
            options={options.status}
          />
        </Box>
        <SearchField
          variant="big"
          search={filters.search}
          onSearch={(event: React.ChangeEvent<HTMLInputElement>) =>
            onFiltersChange({ search: event.target.value })
          }
          placeholder={i18n.t('common.search_by')}
        />
      </Box>
      <Box
        style={{ flex: '1 1 auto' }}
        component="ul"
        overflowY="auto"
        overflowX="hidden"
        data-scroll-container
        flexGrow
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        {loadingConversations ? (
          <ConversationItemSkeleton />
        ) : (
          <>
            {filteredConversations.length === 0 && (
              <Box height="full" alignItems="center" justifyContent="center">
                <Text.Normal>{i18n.t('ats.messages.messages_table.empty')}</Text.Normal>
              </Box>
            )}
            <InfiniteScroll
              loadMore={fetchMoreConversations}
              pageStart={0}
              hasMore={hasMoreConversations}
              initialLoad={false}
            >
              {filteredConversations.map((item, index) => (
                <ConversationsItem conversation={item} key={index} />
              ))}
            </InfiniteScroll>
          </>
        )}
      </Box>
      <Box
        flexDirection="row"
        alignItems="center"
        width="full"
        paddingY="s12"
        paddingX="s24"
        background="grey200"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{ bottomLeft: 'abs008', bottomRight: 'abs008' }}
      >
        <Text.Normal size="100" color="grey800" weight="medium">
          {i18n.tp('ats.messages.messages_table.showing.items', {
            count: totalConversationsCount,
          })}
        </Text.Normal>
      </Box>
    </Box>
  )
}
